import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import { ProviderPackageItemModel } from '@models/providerPackageItem'
import { AppDispatch } from '@redux/store'
import { handleError } from '../utils'
import { EntityOptions, FetchRequestPayload } from './module'
import { Action, RootReducerState } from './types'

const FETCH_REQUEST = 'realbase/providerPackageItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/providerPackageItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/providerPackageItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/providerPackageItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/providerPackageItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/providerPackageItem/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/providerPackageItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/providerPackageItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/providerPackageItem/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

export type ProviderPackageItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviderPackageItems(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().providerPackageItems.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/provider_package_items.json', options)
      .then(({ data }: { data: ProviderPackageItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PACKAGE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadProviderPackageItem(providerPackageItemId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.providerPackageItems.loadedIds]

    if (providerPackageItemId && !loadedIds.includes(providerPackageItemId)){
      loadedIds.push(providerPackageItemId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/provider_package_items/${providerPackageItemId}.json`, options)
      .then(({ data }: { data: ProviderPackageItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PACKAGE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createProviderPackageItem(
  providerPackageItem: Partial<ProviderPackageItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_package_item: providerPackageItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/provider_package_items.json', options, config)
      .then(({ data }: { data: Partial<ProviderPackageItemModel> }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PACKAGE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateProviderPackageItem(
  providerPackageItem: ProviderPackageItemModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/provider_package_items/${providerPackageItem.id}/duplicate.json`, options, config)
      .then(({ data }: { data: ProviderPackageItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PACKAGE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteProviderPackageItem(providerPackageItem: ProviderPackageItemModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_package_items/${providerPackageItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PACKAGE_ITEM)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(state: ProviderPackageItemModuleState = initialState, action: Action = { type: '' }) {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
